/**
 * The subject and message of a client's report email (decision 883), as the project's Set-up tab and the Publish
 * dialog show them: two boxes that start with the project's (else Teslyn's) wording, a button back to that wording,
 * the placeholders Teslyn fills, and a preview of the email as the client's people read it. What the email carries
 * (decision 884) is chosen beside it. The words' rules are the domain's (`reportEmailWordingProblems`,
 * `fillReportEmailWords`).
 */
import type { ReactElement } from 'react';
import {
  REPORT_EMAIL_CONTENTS,
  REPORT_EMAIL_CONTENT_WORD,
  REPORT_EMAIL_MESSAGE_MAX,
  REPORT_EMAIL_PLACEHOLDERS,
  fillReportEmailWords,
  type ReportEmailContent,
  type ReportEmailValues,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextArea, TextField } from '../components/Fields.tsx';

export interface ReportEmailWordsProps {
  readonly subject: string;
  readonly message: string;
  readonly onSubject: (v: string) => void;
  readonly onMessage: (v: string) => void;
  /** The wording the button goes back to, and its name ("Teslyn's wording", "the project's wording"). */
  readonly fallback: { readonly subject: string; readonly message: string; readonly name: string };
  /** The placeholders' values for the preview. */
  readonly preview: ReportEmailValues;
  readonly disabled?: boolean;
}

export function ReportEmailWords(p: ReportEmailWordsProps): ReactElement {
  const same =
    p.subject.trim() === p.fallback.subject.trim() && p.message.trim() === p.fallback.message.trim();
  return (
    <div className="stack">
      <TextField label="Subject" value={p.subject} onChange={p.onSubject} disabled={p.disabled} />
      <TextArea
        label="Message"
        value={p.message}
        onChange={p.onMessage}
        rows={6}
        disabled={p.disabled}
        hint={`Up to ${REPORT_EMAIL_MESSAGE_MAX} characters. Teslyn adds the link or the PDF, who to reply to and how to stop these emails after it.`}
      />
      {!p.disabled && !same && (
        <div className="row">
          <Button
            variant="quiet"
            onClick={() => {
              p.onSubject(p.fallback.subject);
              p.onMessage(p.fallback.message);
            }}
          >
            {`Use ${p.fallback.name}`}
          </Button>
        </div>
      )}
      <details className="report-email__placeholders">
        <summary className="label">Placeholders Teslyn fills in</summary>
        <ul className="stack">
          {REPORT_EMAIL_PLACEHOLDERS.map((x) => (
            <li key={x.key} className="caption">
              <span className="data">{`{${x.key}}`}</span> {x.words}
            </li>
          ))}
        </ul>
      </details>
      <div className="report-email__preview stack" aria-label="Preview">
        <p className="label">Preview</p>
        <p className="body">
          <strong>{fillReportEmailWords(p.subject, p.preview)}</strong>
        </p>
        <p className="body report-email__message">{fillReportEmailWords(p.message, p.preview)}</p>
      </div>
    </div>
  );
}

/** What the email carries (decision 884). */
export function ReportEmailContentField({
  value,
  onChange,
  disabled,
}: {
  readonly value: ReportEmailContent;
  readonly onChange: (v: ReportEmailContent) => void;
  readonly disabled?: boolean;
}): ReactElement {
  return (
    <SelectField
      label="The email carries"
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={REPORT_EMAIL_CONTENTS.map((c) => [c, REPORT_EMAIL_CONTENT_WORD[c]] as const)}
      hint="A PDF over 15 MB is never attached: the email links to it instead and says why."
    />
  );
}
